import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';

interface ExpandableFieldProps {
  children: React.ReactNode;
  fieldType: string;
  label: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  placeholder?: string;
  fieldProps?: {
    renderExpandedContent?: (value: unknown, setValue: (value: unknown) => void) => React.ReactNode;
    [key: string]: unknown;
  };
}

/**
 * ExpandableField - A reusable component that adds expand functionality to form fields
 */
const ExpandableField: React.FC<ExpandableFieldProps> = ({
  children,
  fieldType,
  label,
  value,
  onChange,
  disabled = false,
  placeholder = '',
  fieldProps = {},
}) => {
  const [showExpandedModal, setShowExpandedModal] = useState(false);
  const [expandedValue, setExpandedValue] = useState<unknown>(null);

  useEffect(() => {
    if (showExpandedModal) {
      setExpandedValue(value);
    }
  }, [showExpandedModal, value]);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowExpandedModal(true);
  };

  const handleSaveExpanded = () => {
    onChange(expandedValue);
    setShowExpandedModal(false);
  };

  const isSpecialEditor = ['json', 'sparkql'].includes(fieldType);

  const renderExpandIcon = () => {
    return (
      <span
        className="expand-icon"
        onClick={handleExpandClick}
        title="Expand to edit in larger view"
        style={{
          position: 'absolute',
          right: '10px',
          top: isSpecialEditor ? '8px' : '50%',
          transform: isSpecialEditor ? 'none' : 'translateY(-50%)',
          cursor: 'pointer',
          zIndex: 10,
          background: 'rgba(255, 255, 255, 0.7)',
          padding: '3px',
          borderRadius: '3px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = 'rgba(240, 240, 240, 0.9)';
          e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
        }}
      >
        <i className="fa fa-expand" style={{ fontSize: '14px' }} />
      </span>
    );
  };

  const renderExpandedField = () => {
    switch (fieldType) {
      case 'text':
        return (
          <TextField
            type="text"
            value={expandedValue || ''}
            onChange={(e) => setExpandedValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            fullWidth
            multiline
            rows={20}
            {...fieldProps}
          />
        );
      case 'textarea':
        return (
          <TextField
            multiline
            rows={20}
            value={expandedValue || ''}
            onChange={(e) => setExpandedValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            fullWidth
            sx={{ height: '100%', minHeight: '60vh' }}
            {...fieldProps}
          />
        );
      case 'sparkql':
      case 'json':
        // The specialized editors should be handled by the parent component
        // through the renderExpandedContent prop
        if (fieldProps.renderExpandedContent) {
          return fieldProps.renderExpandedContent(expandedValue, setExpandedValue);
        }
        return (
          <div
            style={{
              padding: '10px',
              backgroundColor: '#f8f8f8',
              borderRadius: '5px',
              height: '100%',
              minHeight: '60vh',
            }}
          >
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                height: '100%',
                width: '100%',
                margin: 0,
                overflow: 'auto',
              }}
            >
              {typeof expandedValue === 'object'
                ? JSON.stringify(expandedValue, null, 2)
                : expandedValue}
            </pre>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {children}
      {renderExpandIcon()}

      <Dialog
        open={showExpandedModal}
        onClose={() => setShowExpandedModal(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: '95%',
            maxWidth: '95%',
            height: '90vh',
          },
        }}
      >
        <DialogTitle>
          {`Edit ${label || ''}`}
          <IconButton
            onClick={() => setShowExpandedModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: isSpecialEditor ? '0' : '10px',
            height: 'calc(90vh - 130px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              flexGrow: 1,
              overflow: 'hidden',
            }}
          >
            {renderExpandedField()}
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setShowExpandedModal(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveExpanded}
            disabled={disabled}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ExpandableField;
